import { Link } from 'react-router-dom';
import { Shield, Target, TrendingDown, TrendingUp, ArrowUpRight } from 'lucide-react';

export default function BlogPolymarketExitDiscipline() {
  return (
    <div className="min-h-screen bg-dark-900 text-white py-8 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto space-y-10 animate-fade-in">
        <header className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-600/15 border border-emerald-500/40 text-xs text-emerald-100 uppercase tracking-wide">
            <Shield className="w-3 h-3" />
            <span>Exits & risk</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            Exit Discipline on Polymarket: Stop Loss, Take Profit, and the "Don&apos;t Get Chopped" Playbook
          </h1>
          <p className="text-dark-300 text-sm sm:text-base max-w-2xl">
            Most Polymarket traders don&apos;t lose because they&apos;re always wrong. They lose because they&apos;re
            undisciplined on exits: cutting winners early, holding losers forever, or rage-flipping the
            same contract ten times. This guide gives you a clean exit framework you can run manually or
            automate.
          </p>
        </header>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-400" />
            Stop loss: the job is survival, not perfection
          </h2>
          <p className="text-sm text-dark-300">
            A stop loss is not a prediction. It&apos;s a business rule: "If the market proves me wrong beyond a
            threshold, I exit to preserve the bankroll." On Polymarket, your loss limit should be set in
            terms of share price movement relative to your entry.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <span className="font-medium">Tight stops</span> reduce drawdowns but increase chop.
            </li>
            <li>
              <span className="font-medium">Loose stops</span> reduce chop but accept deeper drawdowns.
            </li>
            <li>
              The right setting depends on how volatile the market is and how fast you expect the narrative
              to resolve.
            </li>
          </ul>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            Take profit: define what "enough" looks like
          </h2>
          <p className="text-sm text-dark-300">
            Take profit is where most people sabotage themselves. The goal isn&apos;t to sell the top. The goal
            is to lock in positive expectancy and avoid giving back a clean win during a reversal.
          </p>
          <p className="text-sm text-dark-300">
            A good take-profit rule ties back to your original thesis. If you entered because you believed a
            probability was mispriced, then your profit target should be where the market price reflects
            "fair" probability again.
          </p>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Target className="w-5 h-5 text-primary-400" />
            The anti-chop framework (simple, brutal, works)
          </h2>
          <p className="text-sm text-dark-300">
            If you get chopped to death, you&apos;re probably trading noise. A simple way to reduce chop:
          </p>
          <ol className="text-sm text-dark-300 list-decimal list-inside space-y-2">
            <li>
              Use smaller size on the first entry.
            </li>
            <li>
              Only add size after price confirms in your direction.
            </li>
            <li>
              If you stop out, ban yourself from re-entering that same contract for a fixed cooldown
              window.
            </li>
            <li>
              Don&apos;t stack correlated markets at the same time. If your thesis is "Candidate X wins", five
              adjacent election contracts are not diversification.
            </li>
          </ol>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">How to apply this in BetMimic</h2>
          <p className="text-sm text-dark-300">
            If you&apos;re copying traders, exit rules protect you from two failure modes:
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              A trader enters early and holds through volatility that you can&apos;t stomach.
            </li>
            <li>
              A trader scales out while your account is still fully exposed.
            </li>
          </ul>
          <p className="text-sm text-dark-300">
            Practical setup: keep a moderate stop loss, enable a take profit, and enforce a max exposure cap
            so one config can&apos;t dominate your portfolio.
          </p>
        </section>

        <section className="space-y-3">
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              <ArrowUpRight className="w-4 h-4" />
              Read more Polymarket guides
            </Link>
            <Link
              to="/copy-configs"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600/90 hover:bg-primary-500 transition-colors"
            >
              <ArrowUpRight className="w-4 h-4" />
              Configure risk rules
            </Link>
            <a
              href="https://polymarket.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              <ArrowUpRight className="w-4 h-4" />
              Visit Polymarket.com
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}
